import { Avatar, Box, Breadcrumbs, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material';
import { ReactComponent as ExternalLinkIcon } from '../../assets/external-link-icon.svg';
import { useEffect, useState } from 'react';
import { KindergartenDetailsDto } from '../../common/interfaces/kindergarten.interface';
import { getKindergartenDetails } from '../../api/kindergarten.api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LocationsAndGroupsTab from './components/LocationsAndGroupsTab/LocationsAndGroupsTab';
import { KindergartenDetailsTabs } from '../../common/enums/common.enum';
import { theme, palette } from '../../theme';
import TeachersTab from './components/TeachersTab';
import ChildrenTab from './components/ChildrenTab';
import ParentsTab from './components/ParentTab';
import EventConfigurationTab from './components/EventTab/EventConfigurationTab/EventConfigurationTab';
import { useTranslation } from 'react-i18next';

const KindergartenDetails = () => {
  const { t } = useTranslation();
  const { kindergartenId } = useParams<{ kindergartenId: string }>();

  const [activeTab, setActiveTab] = useState<KindergartenDetailsTabs>(KindergartenDetailsTabs.LocationsAndGroups);
  const [kindergarten, setKindergarten] = useState<KindergartenDetailsDto>();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam !== null) {
      let tabIndex: number | undefined;

      if (!isNaN(Number(tabParam))) {
        tabIndex = Number(tabParam);
      }
      if (tabIndex !== undefined && Object.values(KindergartenDetailsTabs).includes(tabIndex)) {
        setActiveTab(tabIndex as KindergartenDetailsTabs);
      }
    }
  }, [location.search, kindergartenId, navigate]);

  useEffect(() => {
    if (kindergartenId) {
      fetchKindergartenDetails(kindergartenId);
    }
  }, [kindergartenId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: KindergartenDetailsTabs) => {
    setActiveTab(newValue);
    navigate(`/${kindergartenId}/details?tab=${newValue}`);
  };

  const fetchKindergartenDetails = async (id: string) => {
    try {
      const response = await getKindergartenDetails(id);
      setKindergarten(response.results);
    } catch (error) {
      console.error('Failed to fetch kindergarten details', error);
    }
  };

  if (!kindergarten) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const tabs = [
    {
      label: t('kindergarten.tabs.locationsAndGroups'),
      value: KindergartenDetailsTabs.LocationsAndGroups
    },
    { label: t('kindergarten.tabs.teachers'), value: KindergartenDetailsTabs.Teachers },
    { label: t('kindergarten.tabs.children'), value: KindergartenDetailsTabs.Children },
    { label: t('kindergarten.tabs.parents'), value: KindergartenDetailsTabs.Parents },
    { label: t('kindergarten.tabs.eventConfiguration'), value: KindergartenDetailsTabs.EventConfiguration }
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          backgroundColor: palette.surface.tint,
          borderRadius: 2,
          px: 7,
          pt: 7,
          mb: 4
        }}
      >
        <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 2 }}>
          <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            {t('nav.kindergartens')}
          </Link>
          <Typography>{kindergarten.name}</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4
          }}
        >
          {kindergarten.logo && <Avatar src={kindergarten.logo} alt={t('common.logo')} sx={{ width: 60, height: 60 }} />}
          <Box>
            <Typography variant="h1">{kindergarten.name}</Typography>
            {kindergarten.website && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <ExternalLinkIcon style={{ width: 16, height: 16, color: 'text.primary' }} />
                <Box
                  component="a"
                  href={kindergarten.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    display: 'inline-block',
                    cursor: 'pointer'
                  }}
                >
                  {kindergarten.website}
                </Box>
              </Box>
            )}
            {kindergarten.description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1
                }}
              >
                {kindergarten.description}
              </Typography>
            )}
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map(({ label, value }) => (
            <Tab
              key={value}
              label={label}
              value={value}
              sx={{
                ...theme.typography.caption,
                textTransform: 'none',
                color: theme => (activeTab === value ? theme.palette.primary.main : theme.palette.text.secondary)
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box>
        <Box sx={{ px: 7, pb: 4 }}>
          {activeTab === KindergartenDetailsTabs.LocationsAndGroups && kindergartenId && (
            <LocationsAndGroupsTab kindergarten={kindergarten} />
          )}
          {activeTab === KindergartenDetailsTabs.Teachers && kindergartenId && (
            <TeachersTab kindergartenId={kindergartenId} kindergartenName={kindergarten.name} />
          )}
          {activeTab === KindergartenDetailsTabs.Children && kindergartenId && (
            <ChildrenTab kindergartenId={kindergartenId} kindergartenName={kindergarten.name} />
          )}
          {activeTab === KindergartenDetailsTabs.Parents && kindergartenId && (
            <ParentsTab kindergartenId={kindergartenId} kindergartenName={kindergarten.name} />
          )}
          {activeTab === KindergartenDetailsTabs.EventConfiguration && kindergartenId && (
            <EventConfigurationTab kindergartenId={kindergartenId} kindergartenName={kindergarten.name} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KindergartenDetails;
